import type { LiveStatement, Currency, StatementResult } from './live-income-statement';
import type { CatalogTransport } from './warehouse-catalog-client';

const record = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const text = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0 && v.length <= 200;
export function periodDate(value: unknown): string {
  if (typeof value !== 'string') throw new Error('Invalid date');
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
  const iso = match ? `${match[3]}-${match[1].padStart(2, '0')}-${match[2].padStart(2, '0')}` : value;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso) || !Number.isFinite(Date.parse(iso))
    || new Date(iso).toISOString().slice(0, 10) !== iso) throw new Error('Invalid date');
  return iso;
}
const lines = [
  ['totalAssets', 'Total assets'], ['totalLiabilitiesShown', 'Total liabilities'],
  ['totalEquityShown', 'Total equity (including computed earnings)'],
  ['totalLiabilitiesAndEquityShown', 'Total liabilities and equity'],
  ['difference', 'Footing difference (liabilities and equity minus assets)'],
] as const;

/** Consume the API's displayed footing; never sum closing balances or redo accounting. */
export function parseBalanceSheet(value: unknown, currency: Currency, asOf: string): LiveStatement {
  if (periodDate(asOf) !== asOf || !record(value) || value.source !== 'netsuite'
    || value.view !== 'balance_sheet_v1' || value.currency !== currency || value.as_of !== asOf
    || value.basis !== 'posting' || value.truncated !== false || value.limit_applied !== false
    || !Number.isSafeInteger(value.fetchedAt) || (value.fetchedAt as number) <= 0
    || (value.fetchedAt as number) > 253402300799 || !record(value.statement)) throw new Error('Unusable balance sheet');
  const source = value.statement;
  if (source.currency !== currency || source.asOf !== asOf || source.basis !== 'posting'
    || !Array.isArray(source.columns) || !source.columns.length || source.columns.length > 1200
    || !record(source.footing)) throw new Error('Invalid statement');
  const footing = source.footing;
  const seen = new Set<string>();
  let previous = '';
  const columns = source.columns.map((column) => {
    if (!record(column) || !text(column.period_id) || !text(column.period) || seen.has(column.period_id)) throw new Error('Invalid period');
    const endDate = periodDate(column.period_end);
    if (endDate <= previous || endDate > asOf) throw new Error('Unordered period');
    previous = endDate; seen.add(column.period_id);
    const foot = footing[column.period_id];
    if (!record(foot)) throw new Error('Missing footing');
    for (const [key] of lines) if (typeof foot[key] !== 'number' || !Number.isFinite(foot[key])) throw new Error('Invalid footing');
    return { id: column.period_id, label: column.period, endDate, status: 'unknown' as const, foot };
  });
  // A valid period-end with no activity can be absent from the API's column list.
  // Refuse to label a prior-period observation as the requested closing balance.
  if (previous !== asOf) throw new Error('Requested period not supplied');
  const displayed = columns.slice(-21);
  return {
    periods: displayed.map(({ foot: _foot, ...period }) => period),
    statement: {
      id: 'balance-sheet', title: 'Balance sheet · summary', kind: 'closing', currency,
      entity: 'All returned subsidiaries in this base currency, including elimination subsidiaries',
      basis: `Posting-period closing balances through ${asOf} · no FX conversion or CTA`,
      rows: lines.map(([id, label]) => ({ id, label, emphasis: true,
        values: Object.fromEntries(displayed.map((column) => [column.id, {
          status: 'measured' as const, amount: column.foot[id] as number,
          note: `NetSuite balance_sheet_v1 / period ${column.id} ending ${column.endDate} / ${id}. API-reported footing, not an independent reconciliation.`,
        }])),
      })),
    },
    liveSource: { label: 'NetSuite via Finance API — not a warehouse database read', fetchedAt: value.fetchedAt as number,
      note: 'API-backed Balance Sheet summary; up to 21 returned period columns. Includes elimination subsidiaries, without FX conversion or CTA. Footing difference is an API observation, not an independent tie-out. Close status is unknown; no account or transaction detail is implied.',
    },
  };
}

export function createBalanceSheetLoader(transport: CatalogTransport, publish: (state: StatementResult) => void) {
  let generation = 0;
  let controller: AbortController | undefined;
  let disposed = false;
  return {
    async refresh(currency: Currency, asOf: string) {
      if (disposed) return;
      const request = ++generation;
      controller?.abort(); controller = new AbortController(); publish({ state: 'loading' });
      let result: StatementResult;
      try {
        if (!['USD', 'GBP'].includes(currency) || periodDate(asOf) !== asOf) throw new Error('Invalid selection');
        const response = await transport(`/api/finance/balance-sheet?asOf=${asOf}&currency=${currency}&basis=posting`, {
          method: 'GET', credentials: 'same-origin', cache: 'no-store', redirect: 'error', signal: controller.signal,
          headers: { Accept: 'application/json' },
        });
        if (response.status === 401) result = { state: 'unauthenticated' };
        else if (response.status === 403) result = { state: 'denied' };
        else if (!response.ok || response.redirected || !/^application\/json(?:;|$)/i.test(response.headers.get('Content-Type') ?? '')) result = { state: 'error' };
        else result = { state: 'ready', data: parseBalanceSheet(await response.json(), currency, asOf) };
      } catch { result = { state: 'error' }; }
      if (!disposed && request === generation) publish(result);
    },
    dispose() { disposed = true; generation++; controller?.abort(); },
  };
}
